import { useEffect, useRef, type ReactNode } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { FeedbackBanner } from './FeedbackBanner';
import { useFeedbackStore } from '@/store/useFeedbackStore';

/**
 * Watches the route and clears banners on navigation. Split out from
 * FeedbackZone so `useLocation()` — which throws outside a Router — is only
 * ever called when a Router is actually present (see FeedbackZone's
 * useInRouterContext guard below). Several existing tests mount FeedbackZone
 * standalone (no Router) to test unrelated dialogs; this keeps that working.
 */
function RouteChangeDismisser() {
  const location = useLocation();
  const dismissOnRouteChange = useFeedbackStore((s) => s.dismissOnRouteChange);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dismissOnRouteChange();
  }, [location.pathname, dismissOnRouteChange]);

  return null;
}

/**
 * Mounts the feedback banner fixed at the viewport bottom alongside its
 * children. The banner data lives in `useFeedbackStore`, so no provider is
 * needed — this is just placement.
 *
 * Also clears banners on navigation: a stale "saved!" from the page you just
 * left shouldn't follow you to the next one. `dismissOnRouteChange` respects
 * each banner's minimum display time, so a banner that appears right before
 * a navigation isn't yanked before it's readable. Route-change dismissal is
 * a no-op outside a Router (e.g. in isolated component tests) — production
 * always renders FeedbackZone inside the app's Router, via AppShell.
 */
export function FeedbackZone({ children }: { children: ReactNode }) {
  const inRouter = useInRouterContext();

  return (
    <>
      {children}
      {inRouter && <RouteChangeDismisser />}
      <FeedbackBanner />
    </>
  );
}
